import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Paper,
  Container,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from "@material-ui/core";
import FolderIcon from "@material-ui/icons/Folder";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import InfoIcon from "@material-ui/icons/Info";
import AssessmentIcon from "@material-ui/icons/Assessment";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";

// Компоненты разделов
import FilesSection from "./components/FilesSection";
import SubscriptionsSection from "./components/SubscriptionsSection";
import InformationSection from "./components/InformationSection";
import ClientDataSection from "./components/ClientDataSection";
import LeadGenSection from "./components/LeadGenSection";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: 0,
    padding: theme.spacing(0, 2),
  },
  paper: {
    display: "flex",
    minHeight: "calc(100vh - 100px)",
    boxShadow: "none",
    border: "1px solid #E1E6F1",
  },
  sidebar: {
    width: 260,
    backgroundColor: "#F3F5F7",
    borderRight: "1px solid #E1E6F1",
    padding: theme.spacing(2),
  },
  sidebarTitle: {
    marginBottom: theme.spacing(2),
    color: "#1A1E24",
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "28px",
    padding: theme.spacing(0, 1),
  },
  listItem: {
    borderRadius: "6px",
    marginBottom: theme.spacing(1),
    border: "1px solid #E1E6F1",
    "&:hover": {
      backgroundColor: "#E1E6F1",
    },
    "&.active": {
      backgroundColor: "#ffffff",
      color: "#1A1E24",
      fontWeight: 600,
      borderColor: "#ffffff",
      "& .MuiListItemIcon-root": {
        color: "#1A1E24",
      },
      "& .MuiListItemText-primary": {
        fontWeight: 600,
        color: "#1A1E24",
      },
    },
    "&:not(.active)": {
      backgroundColor: "#F3F5F7",
      "& .MuiListItemText-primary": {
        fontWeight: 500,
        color: "#1A1E24",
      },
    },
  },
  listItemIcon: {
    minWidth: 32,
    color: "#54606F",
  },
  listItemText: {
    "& .MuiListItemText-primary": {
      fontSize: "16px",
      fontWeight: 500,
      color: "#1A1E24",
    },
  },
  content: {
    flex: 1,
    padding: 0,
    marginTop: 0,
    backgroundColor: "#ffffff",
    overflow: "auto",
    maxHeight: "calc(100vh - 100px)",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#D0D7E2",
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: "#A1ADBA",
      },
    },
    // Для Firefox
    scrollbarWidth: "thin",
    scrollbarColor: "#D0D7E2 transparent",
  },
  contentTitle: {
    marginBottom: theme.spacing(3),
    color: "#1A1E24",
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "28px",
  },
}));

const AdminPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { section } = useParams();
  const history = useHistory();

  const menuItems = [
    {
      id: "files",
      label: t("Files"),
      icon: <FolderIcon />,
      component: FilesSection
    },    
    {
      id: "subscriptions",
      label: t("Subscriptions"),
      icon: <CardMembershipIcon />,
      component: SubscriptionsSection
    },    
    {
      id: "clientdata",
      label: "Клиентские данные",
      icon: <AssessmentIcon />,
      component: ClientDataSection
    },
    {
      id: "leadgen",
      label: "Лидоген",
      icon: <PersonSearchIcon />,
      component: LeadGenSection
    }
    /*
    {
      id: "information",
      label: t("Information"),
      icon: <InfoIcon />,
      component: InformationSection
    }
    */
  ];

  const isValidSection = menuItems.some(item => item.id === section);
  const activeSection = isValidSection ? section : menuItems[0].id;

  // Держим URL каноничным: /admin или неизвестный раздел -> /admin/<первый раздел>
  useEffect(() => {
    if (!isValidSection) {
      history.replace(`/admin/${menuItems[0].id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const handleSelectSection = (id) => {
    if (id !== section) {
      history.push(`/admin/${id}`);
    }
  };

  const ActiveComponent = menuItems.find(item => item.id === activeSection)?.component;

  return (
    <Container component="main" maxWidth="xl" className={classes.container}>
      <Paper className={classes.paper} elevation={0}>
        {/* Боковая панель */}
        <Box className={classes.sidebar}>
          <Typography className={classes.sidebarTitle}>
            {t("Admin Panel")}
          </Typography>
          <List>
            {menuItems.map((item) => (
              <ListItem
                key={item.id}
                button
                className={`${classes.listItem} ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => handleSelectSection(item.id)}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  className={classes.listItemText}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Основной контент */}
        <Box className={classes.content}>
          {ActiveComponent && <ActiveComponent />}
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminPage;
